<template>
  <Content :title="$t('APPS.DRAFT.CREATE_NEW')">
    <Card v-loading="isLoading">
      <div class="flex-container">
        <Transition name="fade-transform" mode="out-in">
          <AppFilesStep v-if="isUploadFilesStep" />
          <BasicInfoStep v-else-if="isBasicInfoStep" />
          <InAppPurchasesStep v-else-if="isInAppPurchasesStep" />
          <AppInfoStep v-else-if="isAppInfoStep" />
          <SupportInfoStep v-else-if="isDeveloperContactStep" />
          <AppMediaStep v-else-if="isAppMediaStep" />
          <FinalCheckStep v-else-if="isFinalCheckStep" />
        </Transition>

        <VerticalStepper
          class="with-stepper__stepper hide-for-small-only"
          :steps="steps"
          :current-step="draftApp.currentState"
          :last-done-step="draftApp.nextState"
          @step-click="triggerStepperController"
        />
      </div>
    </Card>
  </Content>
</template>

<script>
import VerticalStepper from '@@/common/VerticalStepper';
import Content from '@@/common/layout/Content';
import Card from '@@/common/Card';
import AppFilesStep from './AppFilesStep';
import AppMediaStep from './AppMediaStep';
import AppInfoStep from './AppInfoStep';
import BasicInfoStep from './BasicInfoStep';
import InAppPurchasesStep from './InAppPurchasesStep';
import FinalCheckStep from './FinalCheckStep';
import SupportInfoStep from './SupportInfoStep';
import { APP_CREATION_STEPS, STEPPER_ITEMS } from './common/constants';
import { DRAFT_APP } from '@/store/types';
import { mapActions, mapGetters } from 'vuex';
import layout from '@/mixins/layout';
import controller from '@/utils/controller';
const stepperController = controller('stepClick');

export default {
  name: 'AppCreationWizard',
  components: {
    Card,
    VerticalStepper,
    Content,
    AppFilesStep,
    AppMediaStep,
    AppInfoStep,
    BasicInfoStep,
    FinalCheckStep,
    SupportInfoStep,
    InAppPurchasesStep,
  },
  mixins: [layout],

  data() {
    return {
      isLoading: false,
    };
  },

  computed: {
    ...mapGetters({ draftApp: DRAFT_APP }),

    steps() {
      const { haveIap } = this.draftApp;

      const isDisabled = {
        [APP_CREATION_STEPS.IN_APP_PURCHASE]: !haveIap,
      };

      return STEPPER_ITEMS.map(i => ({ ...i, disabled: isDisabled[i.key] }));
    },

    isUploadFilesStep() {
      return this.checkCurrentStep(APP_CREATION_STEPS.UPLOAD_FILES);
    },

    isBasicInfoStep() {
      return this.checkCurrentStep(APP_CREATION_STEPS.BASIC_INFO);
    },

    isAppInfoStep() {
      return this.checkCurrentStep(APP_CREATION_STEPS.APP_INFO);
    },

    isDeveloperContactStep() {
      return this.checkCurrentStep(APP_CREATION_STEPS.SUPPORT_INFO);
    },

    isAppMediaStep() {
      return this.checkCurrentStep(APP_CREATION_STEPS.APP_MEDIA);
    },

    isFinalCheckStep() {
      return this.checkCurrentStep(APP_CREATION_STEPS.FINAL_CHECK);
    },

    isInAppPurchasesStep() {
      return this.checkCurrentStep(APP_CREATION_STEPS.IN_APP_PURCHASE);
    },
  },

  provide() {
    return {
      draftApp: this.draftApp,
      stepperController,
    };
  },

  created() {
    this.fetchData();
    this.setPageTitle('APPS.DRAFT.CREATE_NEW');
  },

  methods: {
    ...mapActions({ fetchDraftAppData: DRAFT_APP }),

    fetchData() {
      const id = this.$route.params?.appId;
      this.isLoading = true;

      return this.fetchDraftAppData(id)
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    checkCurrentStep(step) {
      return this.draftApp?.currentState === step;
    },

    triggerStepperController(index) {
      stepperController.stepClick(index);
    },
  },
};
</script>

<style lang="scss">
.with-stepper__stepper {
  flex-shrink: 0;
  margin-left: $global-margin * 2;

  @include rtl() {
    margin-left: 0;
    margin-right: $global-margin * 2;
  }
}
</style>
